import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
  Chip,
  Divider
} from '@mui/material';
import {
  CloudDownload as CloudDownloadIcon,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Storage as StorageIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.paceestate.com';

function Backup() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', data: null });
  const [storageInfo, setStorageInfo] = useState({
    totalBackups: 0,
    estimatedSize: 0,
    lastBackup: null
  });

  useEffect(() => {
    loadBackupHistory();
  }, []);

  const loadBackupHistory = () => {
    // Load backup history from localStorage
    const history = JSON.parse(localStorage.getItem('backupHistory') || '[]');
    setBackups(history);

    if (history.length > 0) {
      const totalSize = history.reduce((sum, b) => sum + (b.size || 0), 0);
      setStorageInfo({
        totalBackups: history.length,
        estimatedSize: totalSize,
        lastBackup: history[0]?.created_at
      });
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleCreateBackup = async () => {
    setCreateLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/export/full-backup`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          onDownloadProgress: (progressEvent) => {
            // Optional: show download progress
            if (progressEvent.lengthComputable) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              console.log(`Download progress: ${percentCompleted}%`);
            }
          }
        }
      );

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = `paceestate_backup_${format(new Date(), 'yyyyMMdd_HHmmss')}.zip`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Save to backup history
      const newBackup = {
        id: Date.now(),
        filename,
        created_at: new Date().toISOString(),
        size: response.data.size,
        status: 'completed'
      };

      const history = JSON.parse(localStorage.getItem('backupHistory') || '[]');
      history.unshift(newBackup);
      // Keep only last 10 backups in history
      const updatedHistory = history.slice(0, 10);
      localStorage.setItem('backupHistory', JSON.stringify(updatedHistory));

      loadBackupHistory();
      setSuccess('Backup created successfully! Download started.');
    } catch (err) {
      console.error('Backup error:', err);
      setError(err.response?.data?.error || 'Failed to create backup');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteBackup = (backup) => {
    setConfirmDialog({
      open: true,
      type: 'delete',
      data: backup
    });
  };

  const confirmDelete = () => {
    const history = JSON.parse(localStorage.getItem('backupHistory') || '[]');
    const updatedHistory = history.filter(b => b.id !== confirmDialog.data.id);
    localStorage.setItem('backupHistory', JSON.stringify(updatedHistory));
    loadBackupHistory();
    setConfirmDialog({ open: false, type: '', data: null });
    setSuccess('Backup record removed from history');
  };

  const handleRestoreBackup = (backup) => {
    setConfirmDialog({
      open: true,
      type: 'restore',
      data: backup
    });
  };

  const confirmRestore = () => {
    // Note: Actual restore would require backend implementation
    setError('Restore functionality requires uploading the backup file to the server. Please contact your administrator.');
    setConfirmDialog({ open: false, type: '', data: null });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Backup & Restore
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Create full system backups and manage backup history
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Storage Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <StorageIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Total Backups</Typography>
              </Box>
              <Typography variant="h3" color="primary">
                {storageInfo.totalBackups}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                backup records
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CloudDownloadIcon sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="h6">Estimated Size</Typography>
              </Box>
              <Typography variant="h3" color="success.main">
                {formatBytes(storageInfo.estimatedSize)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                total storage
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ScheduleIcon sx={{ mr: 1, color: 'info.main' }} />
                <Typography variant="h6">Last Backup</Typography>
              </Box>
              <Typography variant="h3" color="info.main">
                {storageInfo.lastBackup ? format(new Date(storageInfo.lastBackup), 'MMM dd') : '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {storageInfo.lastBackup ? format(new Date(storageInfo.lastBackup), 'yyyy, HH:mm') : 'No backups yet'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Create Backup Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Create New Backup
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Create a complete backup of all your data including sales, inventory, leads, invoices, and more.
          The backup will be downloaded as a ZIP file containing CSV, JSON, and Excel formats.
        </Typography>

        {createLoading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Creating backup... This may take a few moments for large datasets.
            </Typography>
          </Box>
        )}

        <Button
          variant="contained"
          size="large"
          startIcon={createLoading ? <CircularProgress size={20} /> : <CloudDownloadIcon />}
          onClick={handleCreateBackup}
          disabled={createLoading}
        >
          {createLoading ? 'Creating Backup...' : 'Create Full Backup'}
        </Button>
      </Paper>

      {/* Backup History */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Backup History
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Recent backup records (files are downloaded to your computer)
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {backups.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <StorageIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No Backup History
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create your first backup to get started
            </Typography>
          </Box>
        ) : (
          <List>
            {backups.map((backup, index) => (
              <React.Fragment key={backup.id}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">
                          {backup.filename}
                        </Typography>
                        <Chip
                          size="small"
                          label={backup.status}
                          color={backup.status === 'completed' ? 'success' : 'default'}
                          icon={<CheckCircleIcon />}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Created: {format(new Date(backup.created_at), 'MMM dd, yyyy HH:mm:ss')}
                        </Typography>
                        {backup.size && (
                          <Typography variant="body2" color="text.secondary">
                            Size: {formatBytes(backup.size)}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDeleteBackup(backup)}
                      sx={{ ml: 1 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < backups.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Auto-Backup Settings Info */}
      <Alert severity="info" icon={<WarningIcon />} sx={{ mt: 3 }}>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          Backup Best Practices
        </Typography>
        <Typography variant="body2">
          • Create regular backups before major system changes
          <br />
          • Store backups in a secure, off-site location
          <br />
          • Test backup restoration periodically
          <br />
          • Keep multiple backup versions for redundancy
          <br />
          • Configure automatic backups in Settings for scheduled protection
        </Typography>
      </Alert>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, type: '', data: null })}
      >
        <DialogTitle>
          {confirmDialog.type === 'delete' ? 'Delete Backup Record?' : 'Restore from Backup?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.type === 'delete' ? (
              <>
                Are you sure you want to remove this backup record from history?
                <br />
                <strong>Note:</strong> This only removes the record. If you downloaded the file, it will still be on your computer.
              </>
            ) : (
              <>
                Restoring from a backup will replace all current data with the backup data.
                This action cannot be undone.
                <br />
                <br />
                <strong>Are you absolutely sure you want to continue?</strong>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, type: '', data: null })}>
            Cancel
          </Button>
          <Button
            onClick={confirmDialog.type === 'delete' ? confirmDelete : confirmRestore}
            color={confirmDialog.type === 'delete' ? 'error' : 'warning'}
            variant="contained"
          >
            {confirmDialog.type === 'delete' ? 'Delete' : 'Restore'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Backup;
